import { isApiFetchError } from '@borradh-workspace/api-client';
import {
  VIDEO_GENERATION_FAILED_MESSAGE,
  type VideoGenerationErrorCode,
  VideoGenerationErrorCodes,
  isVideoGenerationErrorCode,
} from '@borradh-workspace/api-client/types';
import { toast } from 'sonner';

import { logWarning } from '@/lib/log-error';

/**
 * A Gallery video that couldn't be made, told to the owner as one standard
 * message plus a VID-xxxx support code (the code table is beside
 * `VideoGenerationErrorCodes` in packages/features).
 */

/** The call of Generate that failed. */
export type VideoGenerateStep = 'copy' | 'save' | 'queue';

/**
 * The support code for a failed Generate. The API names the reason when it
 * refuses a render (`code` on the error); otherwise the failed step does.
 */
export function videoGenerateFailureCode(
  step: VideoGenerateStep | null,
  error: unknown
): VideoGenerationErrorCode {
  const apiCode = (error as { code?: unknown } | null | undefined)?.code;
  if (isVideoGenerationErrorCode(apiCode)) return apiCode;
  if (step === 'copy') return VideoGenerationErrorCodes.COPY_FAILED;
  if (step === 'save') return VideoGenerationErrorCodes.SAVE_FAILED;
  if (step === 'queue' && isApiFetchError(error) && error.status >= 500) {
    return VideoGenerationErrorCodes.QUEUE_FAILED;
  }
  return VideoGenerationErrorCodes.UNEXPECTED;
}

/**
 * Log the failure under its code (PostHog, Sentry and Better Stack, with the
 * API request id to find the server's row) and show the owner the toast.
 */
export function reportVideoGenerateFailure(
  step: VideoGenerateStep | null,
  error: unknown,
  context: { templateId: string | null }
): VideoGenerationErrorCode {
  const code = videoGenerateFailureCode(step, error);
  logWarning('video.generate', `Video not generated (${code})`, {
    feature: 'videos',
    extra: {
      errorCode: code,
      step,
      templateId: context.templateId,
      ...(isApiFetchError(error) && {
        apiStatus: error.status,
        apiPath: error.path,
        requestId: error.requestId,
      }),
    },
  });
  const message =
    code === VideoGenerationErrorCodes.NO_FOOTAGE
      ? 'No matching footage is available for this service. Select or upload a clip to make this video.'
      : VIDEO_GENERATION_FAILED_MESSAGE;
  toast.error(message, {
    description: `Error code: ${code}`,
  });
  return code;
}
